/**
 * Klaverjassen's view of the table.
 *
 * Third adapter onto `TrickTable`, alongside `BoerenbridgeTable` and
 * `HeartsTable`. Under each name goes what that *team* has taken, because this
 * is the only one of the three where the number beside your own face is not
 * yours alone.
 */

import type { KlaverjasLiveState } from '@shared/types/klaverjasLive';
import { SUIT_SYMBOLS, teamOf } from '@shared/game-logic';
import TrickTable, { type TableView } from '@/components/TrickTable';
import { SUIT_LABELS } from '@/lib/labels';

export default function KlaverjasTable({
  state,
  mySeat,
}: {
  state: KlaverjasLiveState;
  mySeat: number | null;
}) {
  const choosing = state.phase === 'choosing';

  const view: TableView = {
    players: state.players.map((player) => {
      const team = teamOf(player.seat);
      return {
        seat: player.seat,
        name: player.name,
        avatar: player.avatar,
        emoji: player.emoji,
        isConnected: player.isConnected && player.isClaimed,
        handCount: player.handCount,
        detail: choosing ? (
          <span
            className={
              state.chooserSeat === player.seat
                ? 'text-accent-400'
                : state.passed.includes(player.seat)
                  ? 'text-slate-600'
                  : 'text-slate-500'
            }
          >
            {state.chooserSeat === player.seat
              ? 'kiest…'
              : state.passed.includes(player.seat)
                ? 'past'
                : '—'}
          </span>
        ) : (
          // The team's points, not this player's: klaverjassen is two against
          // two and there is no such thing as your own score.
          <span className="tabular-nums">{state.taken[team]}</span>
        ),
        badge:
          state.dealerSeat === player.seat ? (
            <span className="rounded bg-base-600 px-1 text-[0.65rem] text-slate-300">D</span>
          ) : state.playingTeam === team && !choosing ? (
            <span className="rounded bg-accent-500/25 px-1 text-[0.65rem] text-accent-200">
              speelt
            </span>
          ) : undefined,
        spoken: `${player.name}, team met ${state.taken[team]} punten deze gift`,
      };
    }),
    currentTrick: state.currentTrick,
    lastTrick: state.lastTrick,
    // Roem is counted from the four cards that lay together, so it belongs with
    // the trick you are looking back at rather than only in the totals.
    lastTrickNote:
      state.lastTrick && state.lastTrick.roem > 0 ? `${state.lastTrick.roem} roem` : undefined,
    // During the choosing phase the ring should follow whoever is deciding.
    turnSeat: state.turnSeat ?? state.chooserSeat,
    scoring: state.phase === 'scoring',
    emptyLabel: choosing ? 'troef kiezen…' : 'wie komt uit?',
    spokenHeader: state.trump ? `Troef: ${SUIT_LABELS[state.trump]}.` : 'Nog geen troef.',
    corner: state.trump ? (
      <span className="rounded-full bg-base-900/80 px-2.5 py-1 text-xs text-slate-300 backdrop-blur">
        <span
          className={state.trump === 'hearts' || state.trump === 'diamonds' ? 'text-red-400' : ''}
        >
          {SUIT_SYMBOLS[state.trump]}
        </span>{' '}
        troef
      </span>
    ) : (
      <span className="rounded-full bg-base-900/80 px-2.5 py-1 text-xs text-slate-300 backdrop-blur">
        troef nog niet gekozen
      </span>
    ),
  };

  return <TrickTable view={view} mySeat={mySeat} />;
}
